import { useState, useEffect } from 'react';
import { Brain, Lightbulb, FileText, Target, RefreshCw } from 'lucide-react';
import { getExtractions, getInsights } from '../lib/api';
import type { Activity } from '../lib/types';

const activityConfig = {
  capture: { icon: Brain, color: 'text-brain-400' },
  insight: { icon: Lightbulb, color: 'text-yellow-400' },
  extraction: { icon: FileText, color: 'text-purple-400' },
  goal_update: { icon: Target, color: 'text-green-400' },
};

export function ActivityFeed() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadActivities = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch from multiple sources and combine into activity feed
      const [extractionsRes, insightsRes] = await Promise.all([
        getExtractions(10),
        getInsights(),
      ]);

      const activities: Activity[] = [];

      // Add extractions
      if (extractionsRes.success && extractionsRes.data) {
        for (const extraction of extractionsRes.data.extractions) {
          activities.push({
            id: `extraction-${extraction.id}`,
            type: 'extraction',
            title: `Processed ${extraction.source_type} content`,
            description: `Extracted ${extraction.decisions.length} decisions, ${extraction.action_items.length} action items`,
            source: 'synthesizer',
            timestamp: extraction.created_at,
          });
        }
      }

      // Add insights
      if (insightsRes.success && insightsRes.data) {
        for (const insight of insightsRes.data.insights) {
          activities.push({
            id: `insight-${insight.id}`,
            type: 'insight',
            title: insight.title,
            description: insight.description,
            source: insight.source,
            timestamp: insight.created_at,
          });
        }
      }

      // Sort by timestamp descending
      activities.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setActivities(activities.slice(0, 20));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activities');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadActivities();
  }, []);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Recent Activity</h2>
        <button
          onClick={loadActivities}
          disabled={isLoading}
          className="p-2 text-gray-400 hover:text-gray-200 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="card border-red-900 bg-red-950/50">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {!isLoading && activities.length === 0 && (
        <div className="card text-center py-8">
          <Brain className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500">No recent activity</p>
        </div>
      )}

      <div className="space-y-2">
        {activities.map((activity) => {
          const config = activityConfig[activity.type];
          const Icon = config.icon;

          return (
            <div
              key={activity.id}
              className="card py-3 flex items-start gap-3"
            >
              <div className={`p-1.5 rounded-lg bg-gray-800 ${config.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium">{activity.title}</p>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatTime(activity.timestamp)}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {activity.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
